import type { App, ObjectPlugin } from 'vue';
import AuthClient, { hybridAuthClient } from './clients/hybridAuthClient';
import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { getBootstrapTasks, getNextBootstrapRoute } from '@/modules/login/login.utils';
import { RouteNames } from '@/router/routeNames';

/*
 * returns the installed authentication client
 */
export const getAuthClient = () => hybridAuthClient;

/*
 * authentication navguard to be used by the global nav guard in the vue route
 */
export const authenticationGuard: NavigationGuardWithThis<undefined> = async (
  to: RouteLocationNormalized,
  _: RouteLocationNormalized
) => {
  const isPostLogoutPorch =
    to.name === RouteNames.POST_LOGOUT || to.path.includes('postLogoutPorch');
  const isRouteRedirect =
    !!(to.query.code && to.query.state) || to.query.error || to.query.errorCode;
  const toRouteRequiresAuth =
    to.matched.some((record) => record.meta.requiresAuth) && !isRouteRedirect && !isPostLogoutPorch;

  if (toRouteRequiresAuth) {
    const authClient = getAuthClient();
    const isAuthenticated = await authClient.isAuthenticated();
    if (!isAuthenticated) {
      try {
        const originalUriTracker = to.fullPath;
        window.localStorage.setItem('deeplinkroute', originalUriTracker);

        // set sourceId
        const source = Array.isArray(to.query.source) ? to.query.source[0] : to.query.source;
        const srcId = (source ?? '') || window.localStorage.getItem('sourceId');
        if (srcId) {
          window.localStorage.setItem('sourceId', srcId);
        }

        await authClient.login();
        // this only runs on mobile
        // this handles post login, instead of the redirect page
        const tasks = await getBootstrapTasks();
        const nextRoute = await getNextBootstrapRoute(tasks);
        return nextRoute;
      } catch (error: unknown) {
        // TODO: handle error or log in DT
        await authClient.logout();
        // TODO: consider .push({ name: RouteNames.LOGIN_LANDING_PAGE }) instead of returning false, but this can cause issues with the navigation pipeline
        if ((error as { errorMessage?: string }).errorMessage === 'user canceled auth') {
          // TODO In Android device this can be recreated by ending session then killing app and relaunch app.
          return false;
        } else {
          return new Error('Authentication Error'); // * authentication error
        }
      }
    } else {
      const tasks = await getBootstrapTasks();
      const nextBootstrapRoute: Partial<RouteLocationNormalized> =
        await getNextBootstrapRoute(tasks); // * user authenticated; move on to the next hook in the navigation pipeline
      let nextAuthGuardRoute: Partial<RouteLocationNormalized> | boolean = true;
      if (tasks.length) {
        const bootstrapRoute =
          to.name === nextBootstrapRoute.name || to.path === nextBootstrapRoute.path;
        nextAuthGuardRoute = bootstrapRoute || nextBootstrapRoute;
      }
      return nextAuthGuardRoute;
    }
  } else {
    return true; // * route does not need authentication
  }
};

export const authPlugin: ObjectPlugin = {
  install: (vueApp: App): void => {
    const authClient = getAuthClient();
    (vueApp.config.globalProperties as unknown as { $auth?: typeof authClient }).$auth = authClient;
  },
};

export default {
  initialize: () => AuthClient.initialize(),
  config: {
    setVaultErrorHandler: hybridAuthClient.setVaultErrorHandler,
  },
};
